import { createContext, useContext, useEffect, useRef, useState } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import { MenuItemContent } from '@deere/fuel-react/MenuItemContent.js';
import { useFuelAutocompleteProps } from '@deere/fuel-react/useFuelAutocompleteProps.js';
import '@deere/fuel-web-components/fuel-button.js';
import '@deere/fuel-web-components/fuel-icon-button.js';
import '@deere/fuel-web-components/fuel-typography.js';
import {
  getSuppliersSroSbmMap,
  searchSuppliersByQuery
} from '../shared/APIHelper';
import {
  buildPasteSummary,
  CANONICAL_SUPPLIER_NUMBER_LENGTH,
  mapSupplierResult,
  MAX_SUPPLIERS,
  normalizeSupplierNumber,
  parsePastedSuppliers
} from './supplierSelectionUtils';
import { getFormattedUserNameWithId } from '../shared/UserLogin';

const MIN_SEARCH_CHARS = 3;

const SupplierPickerContext = createContext({
  selectedCount: 0,
  onDone: () => {}
});

function SupplierPickerPaper({ children, ...paperProps }) {
  const { selectedCount, onDone } = useContext(SupplierPickerContext);
  return (
    <Paper {...paperProps}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          columnGap: 8,
          rowGap: 1,
          flexWrap: 'wrap',
          px: 1.5,
          py: 1
        }}
      >
        <fuel-typography
          variant="body-small"
          style={{ display: 'block', flexShrink: 0, whiteSpace: 'nowrap' }}
        >
          {selectedCount} of {MAX_SUPPLIERS} max selected
        </fuel-typography>
        <fuel-typography variant="body-small" style={{ display: 'block' }}>
          Tip: paste a list from Excel (comma, space or line separated)
        </fuel-typography>
      </Box>
      {children}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 1.5, py: 1 }}>
        <fuel-button
          variant="tertiary"
          onMouseDown={(event) => event.preventDefault()}
          onClick={onDone}
        >
          Done
        </fuel-button>
      </Box>
    </Paper>
  );
}

export default function SupplierMultiSelect({
  suppliers,
  onChange,
  setStatusErrorMsg
}) {
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isPasteLoading, setIsPasteLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [pasteSummary, setPasteSummary] = useState('');
  const requestSequence = useRef(0);
  const pasteSequence = useRef(0);
  const suppliersRef = useRef(suppliers);
  suppliersRef.current = suppliers;
  const fuelAutocompleteProps = useFuelAutocompleteProps({
    multiple: true,
    disableClearable: true
  });

  useEffect(() => {
    const sequence = ++requestSequence.current;
    if (inputValue.trim().length < MIN_SEARCH_CHARS) {
      setOptions([]);
      setIsLoading(false);
      return undefined;
    }

    const timer = setTimeout(async () => {
      if (sequence !== requestSequence.current) return;
      setIsLoading(true);
      try {
        const results = await searchSuppliersByQuery(inputValue.trim());
        const mapped = (results ?? []).map(mapSupplierResult);
        if (sequence !== requestSequence.current) return;
        let sbmMap = new Map();
        if (mapped.length) {
          try {
            sbmMap = await getSuppliersSroSbmMap(
              mapped.map((supplier) => supplier.supplierNumber)
            );
          } catch (error) {
            if (sequence !== requestSequence.current) return;
            setStatusErrorMsg(
              "Couldn't load Supplier Base Manager names.",
              error
            );
          }
        }
        if (sequence !== requestSequence.current) return;
        setOptions(
          mapped.map((supplier) => ({
            ...supplier,
            sbmName: sbmMap.get(supplier.supplierNumber)
              ? getFormattedUserNameWithId(
                  sbmMap.get(supplier.supplierNumber),
                  sbmMap.get(supplier.supplierNumber).userId
                )
              : ''
          }))
        );
      } catch (error) {
        if (sequence !== requestSequence.current) return;
        setOptions([]);
        setStatusErrorMsg("Couldn't fetch Suppliers.", error);
      } finally {
        if (sequence === requestSequence.current) setIsLoading(false);
      }
    }, 275);

    return () => clearTimeout(timer);
  }, [inputValue, setStatusErrorMsg]);

  useEffect(
    () => () => {
      pasteSequence.current += 1;
      requestSequence.current += 1;
    },
    []
  );

  const updateSelection = (nextSelection, fromPaste = false) => {
    if (!fromPaste) {
      pasteSequence.current += 1;
      setIsPasteLoading(false);
    }
    onChange(nextSelection);
    setPasteSummary('');
  };

  const isOptionDisabled = (option) =>
    suppliers.length >= MAX_SUPPLIERS &&
    !suppliers.some(
      (supplier) =>
        normalizeSupplierNumber(supplier.supplierNumber) ===
        normalizeSupplierNumber(option.supplierNumber)
    );

  const handlePaste = async (event) => {
    const pastedText = event.clipboardData.getData('text');
    const tokens = pastedText.split(/[,;\s]+/).filter(Boolean);
    if (!tokens.length || !tokens.every((token) => /^\d+$/.test(token))) {
      if (tokens.length === 1 && !/^\d+$/.test(tokens[0])) {
        event.preventDefault();
        setInputValue(tokens[0]);
        setOpen(true);
      }
      return;
    }

    event.preventDefault();
    const sequence = ++pasteSequence.current;
    requestSequence.current += 1;
    const requestedNumbers = parsePastedSuppliers(pastedText);
    setOpen(false);
    setIsLoading(false);
    setIsPasteLoading(true);
    try {
      const results = await Promise.all(
        requestedNumbers.map(async (supplierNumber) => {
          if (supplierNumber.length > CANONICAL_SUPPLIER_NUMBER_LENGTH)
            return null;
          const matches = await searchSuppliersByQuery(supplierNumber);
          const match = (matches ?? []).find(
            (item) =>
              normalizeSupplierNumber(String(item.supplierNumber)) ===
              supplierNumber
          );
          if (!match) return null;
          const supplier = mapSupplierResult(match);
          const canonicalNumber = normalizeSupplierNumber(
            supplier.supplierNumber
          );
          return {
            ...supplier,
            value: canonicalNumber,
            supplierNumber: canonicalNumber,
            label: `${canonicalNumber} - ${supplier.supplierName}`
          };
        })
      );
      if (sequence !== pasteSequence.current) return;
      const foundSuppliers = results.filter(Boolean);
      const foundNumbers = new Set(
        foundSuppliers.map((supplier) =>
          normalizeSupplierNumber(supplier.supplierNumber)
        )
      );
      const notFound = requestedNumbers.filter(
        (supplierNumber) => !foundNumbers.has(supplierNumber)
      );
      const currentSuppliers = suppliersRef.current;
      const existingNumbers = new Set(
        currentSuppliers.map((supplier) =>
          normalizeSupplierNumber(supplier.supplierNumber)
        )
      );
      const alreadySelected = requestedNumbers.filter((supplierNumber) =>
        existingNumbers.has(supplierNumber)
      ).length;
      const candidates = foundSuppliers.filter(
        (supplier) =>
          !existingNumbers.has(normalizeSupplierNumber(supplier.supplierNumber))
      );
      const slotsAvailable = Math.max(
        0,
        MAX_SUPPLIERS - currentSuppliers.length
      );
      const addedSuppliers = candidates.slice(0, slotsAvailable);
      const skippedForMax = Math.max(0, candidates.length - slotsAvailable);
      updateSelection([...currentSuppliers, ...addedSuppliers], true);
      setPasteSummary(
        buildPasteSummary({
          added: addedSuppliers.length,
          alreadySelected,
          notFound,
          skippedForMax
        })
      );
    } catch (error) {
      if (sequence === pasteSequence.current) {
        setStatusErrorMsg("Couldn't look up pasted supplier numbers.", error);
      }
    } finally {
      if (sequence === pasteSequence.current) setIsPasteLoading(false);
    }
  };

  const handleKeyDown = (event) => {
    if (
      event.key === 'Backspace' &&
      inputValue.length === 0 &&
      suppliers.length > 0
    ) {
      event.preventDefault();
      updateSelection(suppliers.slice(0, -1));
    }
  };

  return (
    <SupplierPickerContext.Provider
      value={{ selectedCount: suppliers.length, onDone: () => setOpen(false) }}
    >
      <Box
        sx={{
          width: '100%',
          minWidth: 0,
          maxWidth: '100%'
        }}
      >
        <Autocomplete
          {...fuelAutocompleteProps}
          fullWidth
          sx={{
            minWidth: 0,
            maxWidth: 'none',
            '&&.MuiAutocomplete-root': {
              width: '100%',
              maxWidth: 'none'
            },
            '&& .MuiTextField-root.MuiFormControl-fullWidth': {
              minWidth: 0,
              maxWidth: 'none'
            },
            '&& .MuiInputBase-root.MuiOutlinedInput-root': {
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              height: 'auto',
              minHeight: 40,
              minWidth: 0,
              maxWidth: 'none',
              gap: 0.5,
              padding: '6px 76px 6px 8px'
            },
            '&& .MuiAutocomplete-input': {
              flex: '1 1 120px',
              minWidth: '120px',
              width: 'auto'
            },
            '&& .MuiAutocomplete-endAdornment': {
              position: 'static',
              transform: 'none',
              height: 'auto',
              margin: 0
            }
          }}
          open={open}
          onOpen={() => setOpen(true)}
          onClose={(_event, reason) => {
            if (
              reason === 'escape' ||
              reason === 'blur' ||
              reason === 'toggleInput'
            ) {
              setOpen(false);
            }
          }}
          value={suppliers}
          inputValue={inputValue}
          options={options}
          loading={isLoading || isPasteLoading}
          filterOptions={(availableOptions) => availableOptions}
          openOnFocus
          getOptionLabel={(option) => option.label}
          isOptionEqualToValue={(option, value) =>
            normalizeSupplierNumber(option.supplierNumber) ===
            normalizeSupplierNumber(value.supplierNumber)
          }
          getOptionDisabled={isOptionDisabled}
          onChange={(_event, nextValue) => {
            updateSelection(nextValue.slice(0, MAX_SUPPLIERS));
            setOpen(true);
          }}
          onInputChange={(_event, nextValue, reason) => {
            if (reason === 'input' || reason === 'clear') {
              setInputValue(nextValue);
              if (reason === 'input') setOpen(true);
            }
          }}
          slots={{
            ...fuelAutocompleteProps.slots,
            paper: SupplierPickerPaper
          }}
          renderOption={(props, option, { selected }) => {
            const { key, ...optionProps } = props;
            const disabled = isOptionDisabled(option);
            return (
              <MenuItem key={key} {...optionProps} disabled={disabled}>
                <MenuItemContent
                  startIcon={
                    <fuel-icon
                      name={selected ? 'check_box' : 'check_box_outline_blank'}
                      aria-hidden="true"
                    />
                  }
                  label={
                    <Box component="span" sx={{ display: 'flex', minWidth: 0 }}>
                      <Box
                        component="fuel-typography"
                        variant="body-medium"
                        sx={{
                          fontFamily: 'monospace',
                          color: 'text.secondary',
                          mr: 1
                        }}
                      >
                        {option.supplierNumber}
                      </Box>
                      <fuel-typography variant="body-medium" no-wrap="true">
                        {option.supplierName}
                      </fuel-typography>
                    </Box>
                  }
                  endIcon={
                    option.sbmName ? (
                      <Box
                        component="fuel-typography"
                        variant="body-small"
                        sx={{ ml: 1 }}
                      >
                        {option.sbmName}
                      </Box>
                    ) : null
                  }
                />
              </MenuItem>
            );
          }}
          renderTags={(values, getTagProps) => (
            <>
              {values.map((supplier, index) => {
                const { key, ...tagProps } = getTagProps({ index });
                return (
                  <Tooltip key={key} title={supplier.label}>
                    <Chip
                      {...tagProps}
                      label={`${supplier.supplierNumber} – ${supplier.supplierName}`}
                      size="small"
                      deleteIcon={
                        <Box
                          component="span"
                          aria-hidden="true"
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            lineHeight: 1
                          }}
                        >
                          <fuel-icon name="close" />
                        </Box>
                      }
                      aria-label={`Remove ${supplier.supplierNumber} – ${supplier.supplierName}`}
                      sx={{
                        maxWidth: '100%',
                        bgcolor: 'rgba(0, 115, 73, 0.08)',
                        border: '1px solid',
                        borderColor: 'success.main',
                        '& .MuiChip-label': {
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }
                      }}
                    />
                  </Tooltip>
                );
              })}
            </>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              placeholder="Search by Name or Number (3+ chars)"
              inputProps={{
                ...params.inputProps,
                'aria-label': 'Select suppliers',
                role: 'combobox',
                'aria-expanded': open,
                onPaste: handlePaste,
                onKeyDown: (event) => {
                  params.inputProps.onKeyDown?.(event);
                  handleKeyDown(event);
                }
              }}
              slotProps={{
                input: {
                  ...params.InputProps,
                  endAdornment: (
                    <Box
                      sx={{
                        position: 'absolute',
                        right: 4,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        display: 'flex',
                        alignItems: 'center'
                      }}
                    >
                      {isLoading || isPasteLoading ? (
                        <CircularProgress size={16} />
                      ) : null}
                      {suppliers.length > 0 && (
                        <fuel-icon-button size="small">
                          <button
                            type="button"
                            aria-label="Clear all suppliers"
                            onMouseDown={(event) => event.preventDefault()}
                            onClick={() => {
                              updateSelection([]);
                              setInputValue('');
                            }}
                          >
                            <fuel-icon name="close" aria-hidden="true" />
                          </button>
                        </fuel-icon-button>
                      )}
                      {params.InputProps.endAdornment}
                    </Box>
                  )
                }
              }}
            />
          )}
        />
        {suppliers.length > 1 && (
          <Box
            component="fuel-typography"
            variant="body-small"
            sx={{ display: 'block', mt: 1 }}
          >
            Showing combined spend for <strong>{suppliers.length}</strong>{' '}
            suppliers
          </Box>
        )}
        {suppliers.length === MAX_SUPPLIERS && (
          <fuel-typography
            aria-live="polite"
            variant="body-small"
            color="secondary"
            style={{ display: 'block' }}
          >
            Maximum of {MAX_SUPPLIERS} suppliers reached.
          </fuel-typography>
        )}
        <fuel-typography
          aria-live="polite"
          variant="body-small"
          style={{ display: 'block' }}
        >
          {pasteSummary}
        </fuel-typography>
      </Box>
    </SupplierPickerContext.Provider>
  );
}
